import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

import BottomMobileOptionsModal from "@/components/bottom-mobile-options-modal";
import PromotedGameTile, {
  type PromotedGameType,
} from "@/components/promoted-game-tile";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Separator } from "@/components/ui/separator";
import useAnalytics from "@/hooks/use-analytics";
import useAuth from "@/hooks/use-auth";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";

import type { GameType } from "../types";
import { type DataType, QUERY, type VariablesType } from "./graphql";
import { InfoIcon } from "./icons";

function InfoContent() {
  return (
    <p className="text-14/[1.72em] text-heather">
      We base these recommendations on the attraction you are looking at and
      your Morty history.
      <br />
      <br />
      Business owner? You can hide this section on your attractions, get
      featured in sponsored results, and support Morty’s mission.{" "}
      <a
        className="text-rose-red"
        href="https://link.morty.app/e/sponsored"
        rel="nofollow noopener"
        target="_blank"
      >
        Learn more.
      </a>
    </p>
  );
}

function Info() {
  const [isOpen, setIsOpen] = useState(false);

  const isDesktop = useIsBreakPoint("md");

  return (
    <>
      <HoverCard onOpenChange={setIsOpen} open={isOpen}>
        <HoverCardTrigger asChild onClick={() => setIsOpen(true)}>
          <InfoIcon className="ml-2.5 inline cursor-help" />
        </HoverCardTrigger>
        {isDesktop && (
          <HoverCardContent className="rounded-[10px] bg-deep-sea px-5 py-4 shadow-none">
            <h6 className="border-white/10 border-b py-[15px] text-center font-semibold text-16/[1.5em] text-white">
              You might also consider
            </h6>
            <div className="py-5">
              <InfoContent />
            </div>
          </HoverCardContent>
        )}
      </HoverCard>
      {!isDesktop && (
        <BottomMobileOptionsModal
          onOpenChange={setIsOpen}
          open={isOpen}
          title="You might also consider"
        >
          <InfoContent />
        </BottomMobileOptionsModal>
      )}
    </>
  );
}

interface AlsoConsiderProps {
  game: GameType;
}

export default function AlsoConsider({ game }: AlsoConsiderProps) {
  const { track } = useAnalytics();
  const { isUser, queryContext } = useAuth();
  const { data } = useQuery<DataType, VariablesType>(QUERY, {
    ...queryContext({ gameId: game.id, isUser }),
    fetchPolicy: "cache-first",
  });

  const handleClick =
    // biome-ignore lint/suspicious/noExplicitAny: legacy
      (promotion: PromotedGameType, analyticsProps: Record<string, any>) =>
      () => {
        track("Suggested Game Tapped", {
          ...analyticsProps,
          company_id: promotion.location.company.id,
          company_name: promotion.location.company.name,
          game_id: promotion.id,
          game_name: promotion.name,
          is_promoted: promotion.isPromoted,
          location_id: promotion.location.id,
        });
      };

  useEffect(() => {
    // biome-ignore lint/suspicious/noExplicitAny: legacy
    (window as any).TS = {
      token: process.env.TOPSORT_TOKEN,
      url: process.env.TOPSORT_URL,
    };

    // biome-ignore lint/suspicious/noExplicitAny: legacy
    import("@topsort/analytics.js" as any);
  }, []);

  if (!data?.alsoConsider.length) {
    return null;
  }

  return (
    <>
      <Separator className="mb-5 bg-white/20" />
      <div className="mb-5">
        <h3 className="mb-5 text-20 text-white leading-[1.2em] md:text-24">
          You might also consider <Info />
        </h3>
        <div
          className="scrollbar-hidden -mx-6 grid snap-x snap-mandatory grid-cols-[repeat(var(--col-count),89%)] gap-3.5 overflow-auto px-6 md:mx-0 md:grid-cols-2 md:gap-7.5 md:overflow-visible md:px-0"
          style={
            { "--col-count": data.alsoConsider.length } as React.CSSProperties
          }
        >
          {data.alsoConsider.map((g, position) => (
            <PromotedGameTile
              className="snap-center md:nth-of-type-[1n+5]:hidden"
              data-ts-clickable
              data-ts-resolved-bid={g.promotedBidId}
              game={g}
              key={g.id}
              onClick={handleClick(g, { position, source_game_id: game.id })}
            />
          ))}
        </div>
      </div>
    </>
  );
}
